import React, { createContext, useContext, useState, useEffect } from 'react'
import type { User, AuthContextType } from '../types/auth'
import { initiateDiscordLogin, logout as logoutService, validateSession, getCurrentUser } from '../services/auth'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check for existing session on mount
    const checkSession = async () => {
      try {
        // First check local storage
        const storedUser = getCurrentUser()
        if (storedUser) {
          setUser(storedUser)
        }

        // Then validate with backend
        const validatedUser = await validateSession()
        setUser(validatedUser)
      } catch (error) {
        console.error('Session validation error:', error)
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    checkSession()
  }, [])

  const login = () => {
    initiateDiscordLogin()
  }

  const logout = async () => {
    await logoutService()
    setUser(null)
  }

  const refreshAuth = async () => {
    try {
      const validatedUser = await validateSession()
      setUser(validatedUser)
    } catch (error) {
      console.error('Session refresh error:', error)
      setUser(null)
    }
  }

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    refreshAuth,
    isAuthenticated: user !== null,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
